<template>
	<div class="otp-verification-container">
		<div
			style="
				position: fixed;
				top: 20px;
				z-index: 49;
				backdrop-filter: blur(2px);
				display: flex;
				justify-content: center;
			"
			v-if="LoaderStatus"
		>
			<Spinner />
		</div>

		<!-- Header with Mycareer title -->
		<div class="otp-header">
			<img
            	src="https://cvstaticfiles.s3.ap-south-1.amazonaws.com/Hire3xAssests/Logo/SVG/Hire3xwhitebeta.svg"
            	alt=""
          	/>
		</div>

		<!-- Main OTP Verification Card -->
		<div class="otp-card">
			<!-- Title Section -->
			<div class="title-section">
				<h2 class="otp-title">Verify OTP</h2>
				<p class="otp-subtitle">
					Please enter the otp sent to the email :-
					<span class="email-display">{{ maskedEmail }}</span>
				</p>
			</div>

			<!-- Error Message -->
			<div id="message" class="error-message"></div>

			<!-- OTP Input Fields -->
			<div class="otp-input-container">
				<input
					v-for="(digit, index) in otpDigits"
					:key="index"
					:ref="`otp-${index}`"
					class="otp-input"
					type="tel"
					inputmode="numeric"
					maxlength="1"
					v-model="otpDigits[index]"
					@input="handleOtpInput(index, $event)"
					@keydown="handleKeyDown(index, $event)"
					@paste="handlePaste"
				/>
			</div>

			<!-- Resend OTP Section -->
			<div class="resend-section">
				<template v-if="!otpExceeded">
					<span class="resend-text">Didn't receive code? </span>
					<span v-if="timerOut" class="resend-text">
						Resend in
						<span
							v-if="timerOut"
							class="timer-count"
						>{{ formattedTime }}</span>
					</span>
					<button
						v-else
						class="resend-button"
						@click="resendOTP"
						:disabled="LoaderStatus"
					>
						Resend OTP
					</button>
				</template>
				<div v-else class="resend-exceeded">
					<span class="resend-text"
						>OTP has already been sent. You can request a new OTP in
					</span>
					<span class="timer-count"
						>{{ formattedTime }}</span>
				</div>
			</div>

			<!-- Verify Button -->
			<button
				@click="registerCandidate"
				class="verify-button"
				:disabled="!isOtpComplete || LoaderStatus"
			>
				VERIFY
			</button>

			<!-- Back to Login Link -->
			<div class="back-to-login-section">
				<router-link to="/login" class="back-to-login-link">
					<span class="back-arrow">←</span> Back to Login
				</router-link>
			</div>
		</div>
	</div>
</template>

<script>
import {
	registerCand,
	userAccess,
	userInfo,
	userLogin,
	sendCandOtp,
	checkCandOtp,
	sendOTP,
	setMobileToken,
} from "../../utils/apiFunctions";
import {
	getToken,
	clearCookies,
	removeToken,
	getMobileToken,
	checkAndSetAccess,
} from "../../utils/tokenCheck";
import { mapState, mapMutations, mapActions } from "vuex";
const qs = require("qs");
import errorModal from "../../components/mixin/toast";
import Spinner from "../../components/fixedcomponents/Spinner.vue";
import { COOKIE_OPTIONS_SUFFIX } from "@/utils/cookieConfig";
import { Capacitor } from "@capacitor/core";

export default {
	name: "OTPVerification",
	components: {
		Spinner,
	},
	mixins: [errorModal],
	data() {
		return {
			otpDigits: ["", "", "", "", "", ""],
			LoaderStatus: false,
			resendTimer: 0,
			email: "",
			type: "", // 'forgot-password' or 'register'
			timer: null,
			registered: false,
			verifyotp: false,
			show: false,
			otpExceeded: false,
			retryAfter: 0,
			timerOut: false,
			remainingTime: 0,
		};
	},
	computed: {
		...mapState("userVUEX", ["userData"]),
		...mapState("userVUEX", {
			mobileToken: (state) => state.mobileToken,
		}),
		isOtpComplete() {
			return this.otpDigits.every((digit) => digit !== "");
		},
		otpValue() {
			return this.otpDigits.join("");
		},
		maskedEmail() {
			if (!this.email) return "";
			const [localPart, domain] = this.email.split("@");
			if (localPart.length <= 2) {
				return this.email;
			}
			const masked =
				localPart[0] +
				"*".repeat(localPart.length - 2) +
				localPart[localPart.length - 1];
			return `${masked}@${domain}`;
		},
		formattedTime() {
			let seconds = this.remainingTime;
			if (seconds < 60) {
				return `${seconds} ${seconds === 1 ? 'sec' : 'secs'}`;
			}

			const mins = Math.floor(seconds / 60);
			const secs = seconds % 60;

			let result = `${mins} ${mins === 1 ? 'min' : 'mins'}`;

			if (secs > 0) {
				result += ` ${secs} ${secs === 1 ? 'sec' : 'secs'}`;
			}

			return result;
		},
	},
	mounted() {
		// Get email and type from query params
		this.email = this.$route.query.email || "";
		this.type = this.$route.query.type || "reset-password";

		// Pick up otpExceeded and retryAfter from query params (set by Forgotpassword.vue)
		if (this.$route.query.otpExceeded === "true" || this.$route.query.otpExceeded === true) {
			this.otpExceeded = true;
		}
		if (this.$route.query.retryAfter) {
			this.retryAfter = Number(this.$route.query.retryAfter);
		}
		let newQuery = {...this.$route.query};
		delete newQuery.otpExceeded;
		delete newQuery.retryAfter;
		this.$router.replace({ query: newQuery }).catch(() => {});
		if (!this.email) {
			this.errorModal("Error", "Email address is required");
			this.$router.push("/forgot");
			return;
		}

		// Start countdown timer using retryAfter
		if (this.retryAfter) {
			this.timerOut = true;
			this.startResendTimer();
		} 

		// Focus first OTP input
		this.$nextTick(() => {
			if (this.$refs["otp-0"]) {
				this.$refs["otp-0"][0].focus();
			}
		});
	},
	methods: {
		...mapMutations({
			setToken: "userVUEX/SET_TOKEN",
			setRefreshToken: "userVUEX/SET_REFRESH_TOKEN",
		}),
		...mapActions("userVUEX", ["Login"]),
		handleOtpInput(index, event) {
			const value = event.target.value;
			// Only allow numbers
			if (!/^\d*$/.test(value)) {
				this.otpDigits[index] = "";
				return;
			}

			this.otpDigits[index] = value;

			// Auto-focus next input
			if (value && index < 5) {
				this.$nextTick(() => {
					if (this.$refs[`otp-${index + 1}`]) {
						this.$refs[`otp-${index + 1}`][0].focus();
					}
				});
			}
		},
		handleKeyDown(index, event) {
			// Handle backspace
			if (event.key === "Backspace" && !this.otpDigits[index] && index > 0) {
				this.$nextTick(() => {
					if (this.$refs[`otp-${index - 1}`]) {
						this.$refs[`otp-${index - 1}`][0].focus();
					}
				});
			}
		},
		handlePaste(event) {
			event.preventDefault();
			const pastedData = event.clipboardData.getData("text").trim();
			if (/^\d{6}$/.test(pastedData)) {
				this.otpDigits = pastedData.split("");
				// Focus last input
				this.$nextTick(() => {
					if (this.$refs["otp-5"]) {
						this.$refs["otp-5"][0].focus();
					}
				});
			}
		},
		pad(val) {
			return val < 10 ? "0" + val : val;
		},
		startResendTimer() {
			clearTimeout(this.timer);
			this.remainingTime = this.retryAfter;
			this.resendTimer = this.retryAfter;
			this.timer = setInterval(() => {
				if (this.remainingTime > 0) {
					this.remainingTime--;
					this.resendTimer = this.remainingTime;
				} else {
					clearInterval(this.timer);
					this.timerOut = false;
					this.otpExceeded = false;
					this.remainingTime = this.retryAfter;
				}
			}, 1000);
		},
		async resendOTP() {
			this.LoaderStatus = true;
			try {
				let response;
				if (this.type === "register") {
					let payload = {
						email: this.email,
						type: "register-account",
						firstName: this.userData.firstName,
						lastName: this.userData.lastName,
					};
					if (this.userData.code) {
						payload.code = this.userData.code;
					}
					response = await sendCandOtp(payload);
				} else {
					response = await sendOTP(this.email, this.type);
				}

				if (response.status == 200 || response.status == 429) {
					if (response.status == 429) this.otpExceeded = true;
					this.LoaderStatus = false;

					this.retryAfter = response.status == 429
						? response.data.data.cooldownSeconds
						: response.data.data.retryAfter;
					if (this.retryAfter) {
						this.timerOut = true;
						this.startResendTimer();
					}
					if (response.status == 200) {
						this.errorModal("Success", "OTP has been resent to your email");
					}
				}
			} catch (error) {
				this.LoaderStatus = false;
				if (error.response && error.response.status == 429) {
					this.otpExceeded = true;
					this.retryAfter = error.response.data.data.cooldownSeconds;
					if (this.retryAfter) {
						this.timerOut = true;
						this.startResendTimer();
					}
				} else if (error.response && error.response.data) {
					this.errorModal(
						"Error",
						error.response.data.error ||
							"Failed to resend OTP. Please try again."
					);
				} else {
					this.errorModal("Error", "Failed to resend OTP. Please try again.");
				}
			} finally {
				this.LoaderStatus = false;
			}
		},
		async registerCandidate() {
			if (this.type == "reset-password") {
				this.resetPasswordOTP();
				return;
			}
			this.verifyotp = true;
			if (this.isOtpComplete) {
				// this.$refs.sixthInput.blur(); // We don't have this ref setup same way
				this.show = true;
				this.LoaderStatus = true;
				this.verifyotp = false;
				let otpVal = this.otpValue;

				// User's payload structure
				let payload = {
					countryCode: this.userData.countryCode,
					email: this.userData.email,
					firstName: this.userData.firstName,
					lastName: this.userData.lastName,
					otp: Number(otpVal),
					password: this.userData.password,
					phoneNumber: this.userData.phoneNumber,
				};
				if (this.userData.code) {
					payload.code = this.userData.code;
				}

				if (this.$route.query.uuid) {
					payload.uuid = this.$route.query.uuid;
				}

				try {
					const response = await registerCand(payload);
					if (response.status == 200) {
						if (this.timer) clearInterval(this.timer);
						this.show = false;

						this.registered = true;

						if (this.$route.query.uuid) {
							this.$store.commit("userVUEX/setNewUserUsingUUID", true);
						}

						this.registered = true;
						// let userDetails = JSON.parse(JSON.stringify(this.userData));
						await this.loginToProjects();
						this.clearData();
						this.clearOtp();
						// this.$store.commit("userVUEX/setUserData", {});
						this.$store.commit("userVUEX/setresumeClinicuid", null);
					} else if (response.status == 400) {
						this.setNoAccess("OTP didn't match");
						this.clearOtp();
						this.LoaderStatus = false;
					} else {
						this.setNoAccess("Something went wrong!");
						this.clearOtp();
						this.LoaderStatus = false;
					}
				} catch (error) {
					console.error(error);
					this.setNoAccess(
						error.response?.data?.data?.Detail || "Error verifying OTP"
					);
					this.clearOtp();
				}
			}
		},
		async resetPasswordOTP() {
			this.verifyotp = true;
			// Manual validation check for dirty/invalid if using Vuelidate or just check OTP completion
			if (this.isOtpComplete) {
				this.show = true;
				this.LoaderStatus = true;
				this.verifyotp = false;
				let otpVal = this.otpValue;

				let payload = this.userData || {};
				if (this.$route.query.email) {
					payload.email = this.$route.query.email;
				}
				payload.otp = Number(otpVal);
				if(!payload.type) payload.type = this.type;
				try {
					const response = await checkCandOtp(payload);
					if (response.status == 200) {
						clearCookies();
						removeToken();
						this.$store.commit("userVUEX/setUserData", {});
						this.$store.commit("userVUEX/setUserData", payload);
						this.$nextTick(() => {
							this.gotoReset();
						});
					} else if (response.status == 400) {
						this.setNoAccess("OTP didn't match");
						this.clearOtp();
						this.LoaderStatus = false;
					} else {
						this.setNoAccess("Something went wrong!");
						this.clearOtp();
						this.LoaderStatus = false;
					}
				} catch (error) {
					console.error(error);
					this.setNoAccess(
						error.response?.data?.data?.Detail || "Error verifying OTP"
					);
					this.clearOtp();
					this.LoaderStatus = false;
				}
			}
		},
		gotoReset() {
			let routeVal = {
				name: "reset",
			};
			if (this.$route.query.uuid)
				routeVal.query = { uuid: this.$route.query.uuid };
			this.$router.push(routeVal);
		},
		async loginToProjects() {
			let creds = JSON.parse(JSON.stringify(this.userData));
			try {
				let userData = {};
				let usernameVal = creds ? creds.email : this.email.trim();
				if (creds) {
					userData = {
						grant_type: "password",
						client_id: Capacitor.isNativePlatform()
							? "hire3x-app"
							: "vue-test-app",
						username: creds.email,
						password: creds.password,
					};
				} else {
					userData = {
						grant_type: "password",
						client_id: Capacitor.isNativePlatform()
							? "hire3x-app"
							: "vue-test-app",
						username: this.email.trim(),
						password: this.password.trim(),
					};
				}
				let userDetails = {
					keycloak: qs.stringify(userData),
					username: usernameVal,
				};
				// The user provided 'this.userLogin', so we'll adapt to use imported 'userLogin'
				const response = await userLogin(qs.stringify(userData), {
					"content-type": "application/x-www-form-urlencoded;charset=utf-8",
				});

				// if (this.mobileToken) {
				// 	document.cookie = `accessToken=${response.data.access_token};expires=Wed, 14 Jan 2026 12:00:00 UTC;path=/;domain=hire3x.com;`;
				// 	await this.appNotification();
				// }
				// // checkPanelLogin and getProjects are missing, using inline logic from LoginPage/previous implementation
				// but adapting to the flow user expects (token handling)

				if (response.status == 200) {
					document.cookie = `accessToken=${response.data.access_token};${COOKIE_OPTIONS_SUFFIX};`;
					document.cookie = `refreshToken=${response.data.refresh_token};${COOKIE_OPTIONS_SUFFIX};`;

					if (this.mobileToken) {
						await setMobileToken({
							token: getMobileToken(),
							platform: this.platform,
						});
					}

					this.$store.commit("userVUEX/SET_TOKEN", response.data.access_token);
					this.$store.commit(
						"userVUEX/SET_REFRESH_TOKEN",
						response.data.refresh_token
					);
					// Proceed to checking candidate validity and redirecting
					await this.checkAndRedirect();
				}
			} catch (error) {
				this.show = false;
				this.LoaderStatus = false;
				console.log(error);
				if (error.response && error.response.status == 401) {
					this.setNoAccess("Invalid credentials.");
				} else if (error.response && error.response.status == 400) {
					this.setNoAccess(error.response.data.error_description);
				} else {
					this.setNoAccess("Login failed! Please try again.");
				}
			}
		},
		async appNotification() {
			try {
				if (this.mobileToken) {
					await setMobileToken({
						token: getMobileToken(),
						platform: this.platform,
					});
				}
			} catch (error) {
				console.error("Notification error:", error);
			}
		},
		async checkAndRedirect() {
			console.log("in useracees", getToken());
			const response = await userAccess();
			console.log(response.data);
			if (response.status == 200) {
				if (response.data.details && response.data.details.length > 0) {
					response.data.details.forEach((element) => {
						document.cookie = `${element.project}=${JSON.stringify(
							element
						)};${COOKIE_OPTIONS_SUFFIX};`;
					});
				}
				checkAndSetAccess();
				const userInfoRes = await userInfo();
				if (userInfoRes.status == 200) {
					this.$store.commit("userVUEX/setUserInfo", userInfoRes.data.data);
				}
				this.LoaderStatus = false;
				if (this.$route.query.uuid) {
					this.$router.push({
						name: "CandidateDashboard",
						query: { uuid: this.$route.query.uuid },
					});
					return;
				}

				if (localStorage.getItem("uuid")) {
					let type = localStorage.getItem("type");
					let query = {
						uuid: localStorage.getItem("uuid"),
						type: localStorage.getItem("type"),
						licenseKey: localStorage.getItem("licenseKey"),
					};
					if (type == "offer") {
						this.$router.push({
							path: "previewofferletter",
							query: query,
						});
					} else {
						this.$router.push({
							path: "registrationform",
							query: query,
						});
					}
				} else {
					if (
						this.$route.query.redirect &&
						this.$route.query.redirect.startsWith("/u/")
					) {
						this.$router.push(this.$route.query.redirect);
					} else {
						this.$router.push({
							path: "/",
							query: {
								from: "candidate",
							},
						});
					}
				}
			} else if (response.status == 401 || response.status == 403) {
				this.setToken("");
				this.$store.commit("userVUEX/SET_TOKEN", "");
				this.$store.commit("userVUEX/SET_REFRESH_TOKEN", "");
				this.setNoAccess("You don't have access");
			}
		},
		clearData(type) {
			this.verifyotp = false;
			this.resendTimer = 0;
			this.LoaderStatus = false;
			this.email = "";
			this.otpDigits = ["", "", "", "", "", ""];
			if (this.$route.query.from && !type) {
				this.$router.push({
					name: "login",
				});
			}
		},
		clearOtp() {
			this.otpDigits = ["", "", "", "", "", ""];
			if (this.$refs["otp-0"]) {
				this.$refs["otp-0"][0].focus();
			}
		},
		gotologin() {
			let routeVal = { name: "LoginPage" };
			// Name in router is likely 'LoginPage' or 'login' based on previous files, using path usually safer if name unknown
			// User snippet used 'login'. I will try to use path '/login' to be safe or name 'login'
			if (this.$route.query.uuid && this.$route.query.type != "verify") {
				routeVal.query = { uuid: this.$route.query.uuid };
			} else if (!this.$route.query.uuid) {
				this.$store.commit("userVUEX/setresumeClinicuid", null);
				this.$router.push("/login");
				return;
			}
			this.$router.push("/login");
		},
		setNoAccess(message) {
			this.show = false;
			this.LoaderStatus = false;
			this.errorModal("Error", message);
		},
	},
};
</script>

<style scoped>
.otp-verification-container {
	height: 100dvh;
	background: linear-gradient(
		to bottom,
		#20639b 0%,
		#09b7c2 50%,
		#f5f5f5 50%,
		#f5f5f5 100%
	);
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 20px;
}

/* Header Section */
.otp-header {
	width: 100%;
	text-align: center;
	padding: 40px 20px 60px;
}

.app-title {
	color: #ffffff;
	font-size: 25px;
	font-weight: 500;
	margin: 0;
	font-family: "Poppins", sans-serif;
}

/* OTP Card */
.otp-card {
	background: #ffffff;
	border-radius: 12px;
	padding: 40px 30px;
	width: 100%;
	max-width: 450px;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
	margin-top: -40px;
	position: relative;
	z-index: 10;
	height: 100%;
	max-height: 618px;
}

/* Title Section */
.title-section {
	margin-bottom: 30px;
}

.otp-title {
	font-size: 30px;
	font-weight: 600;
	font-family: "lato", sans-serif;
	color: #464646;
	text-align: center;
}

.otp-subtitle {
	font-size: 15px;
	font-weight: 500;
	color: #3a3c40;
	font-family: "montserrat", sans-serif;
	text-align: center;
	margin: 0;
}

.email-display {
	color: #2d5a8a;
	font-weight: 500;
}

/* Error Message */
.error-message {
	color: #dc3545;
	font-size: 14px;
	margin-bottom: 15px;
	min-height: 20px;
	text-align: center;
}

/* OTP Input Container */
.otp-input-container {
	display: flex;
	justify-content: space-between;
	gap: 10px;
	margin-bottom: 25px;
}

.otp-input {
	width: 50px;
	height: 50px;
	border: 1px solid #d0d0d0;
	border-radius: 6px;
	font-size: 24px;
	font-weight: 600;
	text-align: center;
	transition: border-color 0.3s;
}

.otp-input:focus {
	outline: none;
	border-color: #2d5a8a;
	box-shadow: 0 0 0 3px rgba(45, 90, 138, 0.1);
}

.otp-input:disabled {
	background-color: #f5f5f5;
	cursor: not-allowed;
}

/* Resend Section */
.resend-section {
	text-align: center;
	margin-bottom: 25px;
}

.resend-text {
	font-size: 14px;
	color: #666666;
}

.resend-timer {
	background: none;
	border: none;
	color: #666666;
	font-size: 14px;
	cursor: default;
	padding: 0;
	margin-left: 5px;
}

.timer-count {
	color: #dc3545;
	font-weight: 600;
}

.resend-button {
	background: none;
	border: none;
	color: #2d5a8a;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	padding: 0;
	margin-left: 5px;
	text-decoration: underline;
}

.resend-button:hover {
	color: #1e3a5f;
}

.resend-button:disabled {
	color: #999999;
	cursor: not-allowed;
	text-decoration: none;
}

/* Verify Button */
.verify-button {
	width: 100%;
	padding: 14px;
	background-color: #2d5a8a;
	color: #ffffff;
	border: none;
	border-radius: 6px;
	font-size: 16px;
	font-weight: 600;
	cursor: pointer;
	text-transform: uppercase;
	transition: background-color 0.3s;
	margin-bottom: 25px;
}

.verify-button:hover:not(:disabled) {
	background-color: #1e3a5f;
}

.verify-button:disabled {
	background-color: #cccccc;
	cursor: not-allowed;
}

/* Back to Login Section */
.back-to-login-section {
	text-align: center;
}

.back-to-login-link {
	color: #2d5a8a;
	text-decoration: none;
	font-size: 14px;
	font-weight: 500;
	display: inline-flex;
	align-items: center;
	gap: 5px;
}

.back-to-login-link:hover {
	text-decoration: underline;
}

.back-arrow {
	font-size: 16px;
}

/* Responsive Design */
@media (max-width: 768px) {
	.otp-card {
		padding: 30px 20px;
		margin-top: -30px;
	}

	.app-title {
		font-size: 28px;
	}

	.otp-title {
		font-size: 24px;
	}

	.otp-header {
		padding: 30px 20px 50px;
	}

	.otp-input {
		width: 45px;
		height: 45px;
		font-size: 20px;
	}
}

@media (max-width: 480px) {
	.otp-verification-container {
		padding: 10px;
	}

	.otp-card {
		padding: 25px 15px;
	}

	.app-title {
		font-size: 24px;
	}

	.otp-title {
		font-size: 20px;
	}

	.otp-input-container {
		gap: 8px;
	}

	.otp-input {
		width: 40px;
		height: 40px;
		font-size: 18px;
	}
}
</style>
